// DatePicker.tsx – Formik‑friendly version
import React, { forwardRef, useEffect, useId, useState } from 'react';
import CalendarAtom from '../Calendar/CalendarAtom';
import './DatePickerAtom.scss';

/** API that works for BOTH Formik <Field as={…}> and stand‑alone use */
export interface DatePickerAtomProps {
  /** current value (Formik injects this) */
  value?: Date | string | null;
  /** Formik‑style onChange (receives a DOM‑like event) */
  onChange?: (e: React.ChangeEvent<any>) => void;
  
  /** stand‑alone callback (kept for backward compatibility) */
  onDateChange?: (d: Date | null) => void;
  
  /** passed unchanged to Calendar */
  dateFormat?: string;
  selectionMode?: 'single' | 'range' | 'multiple';
  label?: string;
  className?: string;
  disabled?: boolean;
  /** Accessible name for the underlying input, forwarded via `...rest` — used when `label` is empty (no visible `<label>` renders in that case). */
  ariaLabel?: string;

  /** Formik injects these */
  name?: string;
  onBlur?: (e: React.FocusEvent<any>) => void;
}

const DatePicker = forwardRef<HTMLDivElement, DatePickerAtomProps>(
  (
    {
      value = null,
      onChange,
      onDateChange,
      onBlur,
      name,
      dateFormat = 'mm/dd/yy',
      selectionMode = 'single',
      label,
      className,
      ...rest /* anything else you need to forward */
    },
    ref
  ) => {
    const generatedId = useId();
    const fieldId = name ?? generatedId;

    // Helper function to convert value to Date
    const parseValue = (val: Date | string | null): Date | null => {
      if (!val) return null;
      if (val instanceof Date) return val;
      if (typeof val === 'string') {
        const parsed = new Date(val);
        return isNaN(parsed.getTime()) ? null : parsed;
      }
      return null;
    };

    /** keep internal state in sync so Calendar is controlled */
    const [internal, setInternal] = useState<Date | Date[] | null>(() => parseValue(value));

    useEffect(() => {
      const parsedValue = parseValue(value);
      setInternal(parsedValue);
    }, [value, name]);

    /** when user picks a date via CalendarAtom's onDateChange */
    const handleCalendarChange = (picked: Date | Date[] | null) => {
      // For single selection mode, ensure we have a single Date
      const singleDate = Array.isArray(picked) ? picked[0] || null : picked;
      
      // Update internal state
      setInternal(singleDate);

      /* 1. notify Formik if present */
      if (onChange) {
        const eventValue = singleDate ? singleDate.toISOString() : '';
        onChange({
          /* minimal synthetic event Formik needs */
          target: { name, value: eventValue }
        } as any);
      }

      /* 2. backwards‑compat callback */
      if (onDateChange) {
        onDateChange(singleDate);
      }
    };

    // Enhanced onBlur handler that includes the name
    const handleBlur = (e: React.FocusEvent<any>) => {
      if (onBlur) {
        // Ensure the event target has a name for Formik
        const enhancedEvent = {
          ...e,
          target: {
            ...e.target,
            name: name || e.target.name
          }
        };
        onBlur(enhancedEvent);
      }
    };

    return (
      <div className={['date-picker', className].filter(Boolean).join(' ')} ref={ref}>
        {/* `name` doubles as `CalendarAtom`'s `inputId` (see below) — falling back to a
            generated id when the caller doesn't supply one keeps the label associated with
            the rendered `<input>` (Formik callers already pass `name`, so this only changes
            standalone usage). */}
        {label && <label className="date-picker__label" htmlFor={fieldId}>{label}</label>}

        <CalendarAtom
          value={internal}
          onDateChange={handleCalendarChange}
          onBlur={handleBlur}
          dateFormat={dateFormat}
          selectionMode={selectionMode}
          showIcon
          className={className}
          name={fieldId} // Pass name to CalendarAtom (becomes its `inputId`)
          {...rest}
        />
      </div>
    );
  }
);

DatePicker.displayName = 'DatePickerAtom';
export default DatePicker;